'use client';

import Image from 'next/image';
import Link from 'next/link';
import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { useStore } from 'zustand';

import { Setting } from '@payload-types';

import { IconKitCart } from '@/icon/kit/Cart';
import { IconKitProfile } from '@/icon/kit/Profile';
import { IconKitSearchTapbar } from '@/icon/kit/SearchTapbar';
import { IconKitTv } from '@/icon/kit/Tv';
import { useUserStore } from '@/store/user';
import { storeUiModal } from '@/store-ui/modal';
import { UiKitButton } from '@/ui-kit/button';
import { UiKitCounter } from '@/ui-kit/counter';

import { styles } from './UiAppTapbar.styles';

export interface UiAppTapbarProps {
  data: Setting;
  cartCounter?: ReactNode;
}

const SCROLL_SETTLE_MS = 120;
const SCROLL_FOLLOWUP_MS = [100, 300, 600];
const MAX_SHIFT = 120;
const SHIFT_TOLERANCE = 4;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

/** iOS/iPadOS WebKit: нужен JS-сдвиг из-за динамической панели браузера */
function needsTapbarViewportCompensation() {
  if (typeof window === 'undefined' || typeof CSS === 'undefined') return false;
  if (!CSS.supports('-webkit-touch-callout', 'none')) return false;
  if (navigator.maxTouchPoints < 1) return false;

  return (
    /iPhone|iPad|iPod/.test(navigator.platform) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Мобильный, навигационный бар, для десктопа скрыт на уровне стилей */
export function UiAppTapbar({ data, cartCounter }: UiAppTapbarProps) {
  const slots = styles();
  const barRef = useRef<HTMLDivElement>(null);

  const { company } = data;
  const logo = company?.logoIcon;
  const openModal = useStore(storeUiModal, (state) => state.open);
  const isAuthorized = useUserStore((state) => state.isAuthorized);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const root = document.documentElement;
    const reset = () => {
      root.style.removeProperty('--tapbar-shift');
    };

    // Android/Chrome и остальные: нативный fixed bottom-0, без JS-сдвига
    if (!needsTapbarViewportCompensation()) {
      reset();
      return;
    }

    const desktopQuery = window.matchMedia('(min-width: 768px)');
    const visualViewport = window.visualViewport;
    let rafId = 0;
    let scrollSettleTimer = 0;
    const followupTimers: number[] = [];

    const makeProbe = (height: string) => {
      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = `position:fixed;top:0;left:0;width:0;height:${height};pointer-events:none;visibility:hidden`;
      document.body.appendChild(probe);
      return probe;
    };

    const dvhProbe = makeProbe('100dvh');
    const svhProbe = makeProbe('100svh');

    const setShift = (shift: number) => {
      if (Math.abs(shift) < 1) {
        reset();
        return;
      }

      root.style.setProperty('--tapbar-shift', `${shift}px`);
    };

    const apply = () => {
      if (desktopQuery.matches) {
        reset();
        return;
      }

      const dvhDelta = dvhProbe.offsetHeight - svhProbe.offsetHeight;
      const vvBottomGap = visualViewport
        ? Math.max(
            0,
            window.innerHeight - visualViewport.offsetTop - visualViewport.height
          )
        : 0;
      const shift = clamp(Math.round(dvhDelta - vvBottomGap), -MAX_SHIFT, MAX_SHIFT);

      setShift(shift);

      if (el.getClientRects().length === 0) return;

      const rect = el.getBoundingClientRect();
      const viewportBottom = visualViewport
        ? visualViewport.offsetTop + visualViewport.height
        : window.innerHeight;
      const correction = viewportBottom - rect.bottom;

      if (Math.abs(correction) <= SHIFT_TOLERANCE) return;

      setShift(clamp(shift + Math.round(correction), -MAX_SHIFT, MAX_SHIFT));
    };

    const requestApply = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(apply);
      });
    };

    const clearSettleTimers = () => {
      window.clearTimeout(scrollSettleTimer);
      followupTimers.forEach((timerId) => window.clearTimeout(timerId));
      followupTimers.length = 0;
    };

    const applyAfterScroll = () => {
      clearSettleTimers();
      requestApply();
      SCROLL_FOLLOWUP_MS.forEach((delay) => {
        followupTimers.push(window.setTimeout(requestApply, delay));
      });
    };

    const scheduleScrollSettle = () => {
      clearSettleTimers();
      scrollSettleTimer = window.setTimeout(applyAfterScroll, SCROLL_SETTLE_MS);
    };

    apply();

    const resizeObserver = new ResizeObserver(scheduleScrollSettle);
    resizeObserver.observe(dvhProbe);

    visualViewport?.addEventListener('resize', scheduleScrollSettle);
    visualViewport?.addEventListener('scroll', scheduleScrollSettle);
    window.addEventListener('resize', scheduleScrollSettle);
    window.addEventListener('scroll', scheduleScrollSettle, { passive: true });
    window.addEventListener('scrollend', applyAfterScroll, { passive: true });
    window.addEventListener('orientationchange', scheduleScrollSettle);
    window.addEventListener('touchend', applyAfterScroll, { passive: true });
    desktopQuery.addEventListener('change', requestApply);

    return () => {
      cancelAnimationFrame(rafId);
      clearSettleTimers();
      resizeObserver.disconnect();
      dvhProbe.remove();
      svhProbe.remove();
      visualViewport?.removeEventListener('resize', scheduleScrollSettle);
      visualViewport?.removeEventListener('scroll', scheduleScrollSettle);
      window.removeEventListener('resize', scheduleScrollSettle);
      window.removeEventListener('scroll', scheduleScrollSettle);
      window.removeEventListener('scrollend', applyAfterScroll);
      window.removeEventListener('orientationchange', scheduleScrollSettle);
      window.removeEventListener('touchend', applyAfterScroll);
      desktopQuery.removeEventListener('change', requestApply);
      reset();
    };
  }, []);

  return (
    <>
      <div
        className={slots.spacer()}
        aria-hidden
      />
      <div
        ref={barRef}
        className={slots.base()}
        data-app-tapbar
      >
        <div className={slots.container()}>
          <div className={slots.item()}>
            <Link
              href="/online"
              className={slots.linkIconWrapper()}
            >
              <IconKitTv className={slots.linkIcon()} />
            </Link>
          </div>
          <div className={slots.item()}>
            <UiKitButton
              color="transparent"
              className={slots.button()}
              onClick={() => openModal('appMenuMobile', { currentLevel: 1 })}
            >
              <IconKitSearchTapbar className={slots.linkIcon()} />
            </UiKitButton>
          </div>
          <div className={slots.item()}>
            {logo && typeof logo !== 'number' ? (
              <Link href={'/'}>
                <Image
                  key={logo?.url}
                  src={logo?.url ?? ''}
                  alt={logo?.alt}
                  width={logo?.width ?? 92}
                  height={logo?.height ?? 44}
                  className={slots.logo()}
                />
              </Link>
            ) : (
              <div className={slots.companyName()}>{company?.name}</div>
            )}
          </div>
          <div className={slots.item()}>
            {isAuthorized ? (
              <Link
                href="/account"
                className={slots.linkIconWrapper()}
              >
                <IconKitProfile className={slots.linkIcon()} />
                <UiKitCounter
                  prefix="marker"
                  className={slots.linkCounter()}
                />
              </Link>
            ) : (
              <UiKitButton
                color="ghost"
                rounded="full"
                className={slots.button()}
                onClick={() =>
                  openModal('authLogin', {
                    needRedirect: true,
                    placement: 'header',
                    authContext: 'account'
                  })
                }
              >
                <IconKitProfile className={slots.linkIcon()} />
              </UiKitButton>
            )}
          </div>
          <div className={slots.item()}>
            <Link
              href={'/cart'}
              className={slots.linkIconWrapper()}
            >
              <IconKitCart className={slots.linkIcon()} />
              {cartCounter}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
